import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { getTemplateIdFromString } from "@mirage/analytics/events/enums/template_id-utils";
import { PAP_Click_UseTemplateButton } from "@mirage/analytics/events/types/click_use_template_button";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import { useAssistTemplatesContext } from "@mirage/mosaics/ComposeAssistant/data/AssistTemplatesContext";
import { useComposeVoicesContext } from "@mirage/mosaics/ComposeAssistant/data/ComposeVoicesContext";
import {
  getTemplateRewriteUserMessageString,
  getVoiceRewriteUserMessageString,
} from "@mirage/mosaics/ComposeAssistant/data/current-session/ComposeCurrentSessionPostMessage";
import {
  appendManualMessages,
  cancelConversationRequest,
  postUserMessage,
} from "@mirage/service-conversation";
import type { UserMessageOptions } from "@mirage/service-conversation/types";
import { getDashActionSurfaceComponentLoggingSource } from "@mirage/service-conversation/utils/loggingUtils";
import { ComposeParams } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/assist_compose_pb";
import {
  NoSearch,
  SearchParams,
} from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/context_engine_conversations_service_pb";
import { tagged } from "@mirage/service-logging";
import { AccessType } from "@mirage/shared/compose/assist-api";
import {
  isPreConfiguredTemplateID,
  type AssistTemplate,
} from "@mirage/shared/compose/assist-template";
import {
  isPreConfiguredVoiceID,
  type ComposeVoice,
} from "@mirage/shared/compose/compose-voice";
import { useCallback, useEffect, useMemo, useState } from "react";

const logger = tagged("conversation-draft-config");

export interface SelectedTemplate {
  id: string | null;
  name: string;
  content?: string;
}

interface UseConversationDraftConfigProps {
  conversationId?: string;
  markdownDraft?: string;
  model?: string;
  searchParams?: SearchParams;
  setManuallyAddedSources: (sources: ChatSource[]) => void;
  writeAgentPayload?: string;
  setUserMessageOptions: (options: UserMessageOptions) => void;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: {
      actionSurfaceComponent?: ActionSurfaceComponent;
    },
  ) => void;
}

export interface UseConversationDraftConfigResult {
  selectedTemplate: SelectedTemplate | null | undefined;
  setSelectedTemplate: (template: SelectedTemplate) => void;
  selectedVoiceID: string | undefined;
  setSelectedVoiceID: (voiceID: string) => void;
  agentPayload: ComposeParams;
  onPostMessageWithVoice: (voiceID: string) => void;
  onPostMessageWithTemplate: (templateID: string) => void;
  onTemplateChange: (templateID: string | null, templateName: string) => void;
  onVoiceChange: (voiceID: string, voiceName: string) => void;
  manuallyAddedSourceDocumentIds?: string[];
  resetDraftConfigState: () => void;
}

export function useConversationDraftConfig({
  conversationId,
  markdownDraft,
  model,
  searchParams,
  setManuallyAddedSources,
  writeAgentPayload,
  setUserMessageOptions,
  logComposeEvent,
}: UseConversationDraftConfigProps): UseConversationDraftConfigResult {
  const { voices } = useComposeVoicesContext();
  const { templates, defaultTemplates } = useAssistTemplatesContext();
  const [selectedTemplate, setSelectedTemplate] = useState<
    SelectedTemplate | null | undefined // allow null for 'Start from scratch' option
  >(undefined);
  const [selectedVoiceID, setSelectedVoiceID] = useState<string | undefined>(
    undefined,
  );

  /**
   * writeAgentPayload is the last agent payload that was used to generate a draft
   * and is returned from the getUserConversation endpoint to restore the draft state
   */
  useEffect(() => {
    if (!writeAgentPayload) {
      return;
    }

    try {
      const parsedPayload = JSON.parse(writeAgentPayload);

      // Hydrate template state if template_id exists
      if (parsedPayload.template_id && templates && defaultTemplates) {
        const allTemplates = [...templates, ...defaultTemplates];
        const template = allTemplates.find(
          (t: AssistTemplate) => t.id === parsedPayload.template_id,
        );

        if (template) {
          setSelectedTemplate({
            id: template.id,
            name: template.name,
            content: template.templateContent,
          });
        }
      }

      // Hydrate voice state if voice_id exists
      if (parsedPayload.voice_id && voices) {
        const voice = voices.find(
          (v: ComposeVoice) => v.id === parsedPayload.voice_id,
        );
        // if not found in api voices, this is a preconfigured voice
        if (voice || isPreConfiguredVoiceID(parsedPayload.voice_id)) {
          setSelectedVoiceID(parsedPayload.voice_id);
        } else {
          logger.error("Voice not found", parsedPayload.voice_id);
        }
      }
    } catch (error) {
      logger.error("Error parsing writeAgentPayload:", error);
    }
  }, [writeAgentPayload, templates, defaultTemplates, voices]);

  // pre-configured voices don't have voice samples, and only need to
  // send the default voiceId, e.g. "preset_analytical" to the API
  const getCustomVoiceSamples = useCallback(
    (voiceID?: string) => {
      if (!voiceID || isPreConfiguredVoiceID(voiceID)) {
        return [];
      }
      return (
        voices
          ?.find((v) => v.id === voiceID)
          ?.redraftedSamples?.map((s) => s.text) ?? []
      );
    },
    [voices],
  );

  // pre-configured templates don't have template content, and only need to
  // send the default templateId, e.g. "preset_analytical" to the API
  const getCustomTemplateContent = useCallback(
    (templateID?: string | null | undefined) => {
      if (!templateID || isPreConfiguredTemplateID(templateID)) {
        return "";
      }
      return templates?.find((t) => t.id === templateID)?.templateContent ?? "";
    },
    [templates],
  );

  const agentPayload = useMemo(() => {
    return new ComposeParams({
      markdownContent: markdownDraft ?? "",
      customTemplateContent: selectedTemplate?.content ?? "",
      templateId: selectedTemplate?.id ?? "",
      customVoiceSamples: getCustomVoiceSamples(selectedVoiceID),
      voiceId: selectedVoiceID,
    });
  }, [
    selectedTemplate?.content,
    selectedTemplate?.id,
    selectedVoiceID,
    markdownDraft,
    getCustomVoiceSamples,
  ]);

  const getAgentPayloadForRewrite = useCallback(
    (voiceID?: string | undefined, templateID?: string | null | undefined) => {
      let customVoiceSamples: string[] = [];
      let customTemplateContent: string = "";
      if (voiceID) {
        customVoiceSamples = getCustomVoiceSamples(voiceID);
      }
      if (templateID) {
        customTemplateContent = getCustomTemplateContent(templateID);
      }
      return JSON.stringify({
        ...agentPayload,
        voiceId: voiceID ?? "",
        templateId: templateID ?? "",
        customVoiceSamples,
        customTemplateContent,
      });
    },
    [agentPayload, getCustomVoiceSamples, getCustomTemplateContent],
  );

  const onSelectedVoice = useCallback(
    (voiceID: string) => {
      setSelectedVoiceID(voiceID);
      if (conversationId && voices) {
        try {
          // Cancel any ongoing request before starting a new one
          cancelConversationRequest(conversationId);

          const voiceRewriteMsg = getVoiceRewriteUserMessageString(
            voiceID,
            voices,
          );
          const agentPayload = getAgentPayloadForRewrite(
            voiceID,
            selectedTemplate?.id,
          );
          postUserMessage({
            conversationId,
            message: voiceRewriteMsg,
            model: model,
            agentPayload,
            agentIds: ["write"],
            source: getDashActionSurfaceComponentLoggingSource(
              "compose_editor_pane",
            ),
            searchParams,
          });
          setManuallyAddedSources([]);
          setSelectedVoiceID(voiceID);
        } catch (error) {
          logger.error("Error getting voice message string:", error);
        }
      }
    },
    [
      conversationId,
      voices,
      model,
      searchParams,
      setManuallyAddedSources,
      selectedTemplate?.id,
      getAgentPayloadForRewrite,
    ],
  );

  const onSelectedTemplate = useCallback(
    (templateID: string) => {
      if (markdownDraft && defaultTemplates && conversationId) {
        // Cancel any ongoing request before starting a new one
        cancelConversationRequest(conversationId);

        const templateRewriteMsg = getTemplateRewriteUserMessageString(
          templateID,
          templates || [],
          defaultTemplates,
        );
        const agentPayload = getAgentPayloadForRewrite(
          selectedVoiceID,
          templateID,
        );
        postUserMessage({
          conversationId,
          message: templateRewriteMsg,
          model: model,
          agentPayload,
          agentIds: ["write"],
          source: getDashActionSurfaceComponentLoggingSource(
            "compose_editor_pane",
          ),
          searchParams,
        });
        setManuallyAddedSources([]);
        setSelectedTemplate({
          id: templateID,
          name: templateID,
          content: templateID,
        });
      }
    },
    [
      conversationId,
      model,
      searchParams,
      templates,
      defaultTemplates,
      markdownDraft,
      setManuallyAddedSources,
      selectedVoiceID,
      getAgentPayloadForRewrite,
    ],
  );

  const onTemplateChange = useCallback(
    (templateID: string | null, templateName: string) => {
      if (templateID === null) {
        setSelectedTemplate(null);
      } else {
        const allTemplates = [...(templates || []), ...defaultTemplates];
        const template = allTemplates.find(
          (t: AssistTemplate) => t.id === templateID,
        );
        if (template) {
          setSelectedTemplate({
            id: templateID,
            name: templateName,
            content: template.templateContent,
          });
          logComposeEvent(
            PAP_Click_UseTemplateButton({
              templateId: isPreConfiguredTemplateID(template.id)
                ? getTemplateIdFromString(template.id)
                : "user_generated",
              templateType: isPreConfiguredTemplateID(template.id)
                ? "starter_template"
                : template.type === "custom" &&
                    template.accessType === AccessType.INDIVIDUAL
                  ? "user_template"
                  : "company_template",
            }),
          );
        }
      }
      if (conversationId) {
        appendManualMessages([
          {
            conversationId: conversationId,
            message: templateName,
            llmIgnore: true,
            messageType: "userMessage",
            source: getDashActionSurfaceComponentLoggingSource(
              "compose_editor_pane",
            ),
          },
        ]);
        setUserMessageOptions({
          searchParams: new SearchParams({
            searchTarget: {
              value: new NoSearch(),
              case: "noSearch",
            },
          }),
          agentIds: ["write"],
        });
      }
    },
    [
      conversationId,
      templates,
      defaultTemplates,
      setUserMessageOptions,
      logComposeEvent,
    ],
  );

  const onVoiceChange = useCallback(
    (voiceID: string, voiceName: string) => {
      setSelectedVoiceID(voiceID);
      if (conversationId) {
        appendManualMessages([
          {
            conversationId: conversationId,
            message: voiceName,
            llmIgnore: true,
            messageType: "userMessage",
            source:
              getDashActionSurfaceComponentLoggingSource("compose_chat_pane"),
          },
        ]);
        setUserMessageOptions({
          searchParams: new SearchParams({
            searchTarget: {
              value: new NoSearch(),
              case: "noSearch",
            },
          }),
          agentIds: ["write"],
        });
      }
    },
    [conversationId, setUserMessageOptions],
  );

  const resetDraftConfigState = useCallback(() => {
    setSelectedTemplate(undefined);
    setSelectedVoiceID(undefined);
    setManuallyAddedSources([]);
  }, [setManuallyAddedSources]);

  return {
    selectedTemplate,
    setSelectedTemplate,
    selectedVoiceID,
    setSelectedVoiceID,
    agentPayload,
    onPostMessageWithVoice: onSelectedVoice,
    onPostMessageWithTemplate: onSelectedTemplate,
    onTemplateChange,
    onVoiceChange,
    resetDraftConfigState,
  };
}
